import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { toasterMsg } from '../Toaster/Toaster';
import { BombiixModal } from '../BombiixModal';

const DEFAULT_ERROR_SHEET_FILENAME = 'error-sheet.xlsx';
const DOWNLOAD_CLEANUP_DELAY_MS = 100;
const TIMESTAMP_FORMAT_REGEX = /[:.]/g;
const TIMESTAMP_SLICE_END = -5;
const DOWNLOADED_ERROR_SHEETS_KEY = 'downloadedErrorSheets';
const DOWNLOADED_SHEETS_TTL_MS = 24 * 60 * 60 * 1000;

function SupplierUploadErrorModal({ show, onHide, onConfirm, errorSheetUrl, validationMessage }) {
  const { t } = useTranslation('modules/partner-overview');
  const { t: tCommon } = useTranslation('common');
  const isDownloadAttempted = useRef(false);

  useEffect(() => {
    if (!show) {
      isDownloadAttempted.current = false;
    }
  }, [show, errorSheetUrl]);

  const getDownloadedSheets = useCallback(() => {
    try {
      const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
      if (!stored) return new Set();
      const storedData = JSON.parse(stored);
      const now = Date.now();
      const validEntries = storedData.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
      return new Set(validEntries.map((entry) => entry.url));
    } catch {
      return new Set();
    }
  }, []);

  const markSheetAsDownloaded = useCallback((url) => {
    try {
      const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
      const markSheetData = stored ? JSON.parse(stored) : [];
      const now = Date.now();
      const validEntries = markSheetData.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
      validEntries.push({ url, timestamp: now });
      localStorage.setItem(DOWNLOADED_ERROR_SHEETS_KEY, JSON.stringify(validEntries));
    } catch {
      // ignore
    }
  }, []);

  const extractFilenameFromHeaders = useCallback((responseHeaders) => {
    const contentDispositionHeader = responseHeaders?.get?.('Content-Disposition');
    if (!contentDispositionHeader) return DEFAULT_ERROR_SHEET_FILENAME;
    const filenameMatch = contentDispositionHeader.match(/filename="?(.+?)"?$/);
    return filenameMatch ? filenameMatch[1] : DEFAULT_ERROR_SHEET_FILENAME;
  }, []);

  const generateTimestampedFilename = useCallback((originalFilename) => {
    const lastDotIndex = originalFilename.lastIndexOf('.');
    const fileNameWithoutExtension = lastDotIndex > 0 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
    const fileExtension = lastDotIndex > 0 ? originalFilename.substring(lastDotIndex) : '.xlsx';
    const timestamp = new Date().toISOString().replace(TIMESTAMP_FORMAT_REGEX, '-').slice(0, TIMESTAMP_SLICE_END);
    return `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
  }, []);

  const triggerBlobDownload = useCallback((blobData, downloadFilename) => {
    const blobUrl = window.URL.createObjectURL(blobData);
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = downloadFilename;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    setTimeout(() => {
      if (document.body.contains(downloadLink)) {
        document.body.removeChild(downloadLink);
      }
      window.URL.revokeObjectURL(blobUrl);
    }, DOWNLOAD_CLEANUP_DELAY_MS);
  }, []);

  const downloadErrorSheet = useCallback(async () => {
    if (!errorSheetUrl || isDownloadAttempted.current) return;

    const downloadedSheets = getDownloadedSheets();
    if (downloadedSheets.has(errorSheetUrl)) return;

    isDownloadAttempted.current = true;
    const resolvedUrl = typeof errorSheetUrl === 'string' && errorSheetUrl.startsWith('/') ? `${window.location.origin}${errorSheetUrl}` : errorSheetUrl;

    try {
      const response = await fetch(resolvedUrl, { method: 'GET', mode: 'cors', credentials: 'include' });
      if (!response.ok) {
        if (response.status === 404) {
          markSheetAsDownloaded(errorSheetUrl);
          return;
        }
        throw new Error(`Failed to fetch error sheet: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const extractedFilename = extractFilenameFromHeaders(response.headers);
      const timestampedFilename = generateTimestampedFilename(extractedFilename);
      triggerBlobDownload(blob, timestampedFilename);
      markSheetAsDownloaded(errorSheetUrl);
    } catch (error) {
      if (error?.message?.includes('404') || error?.message?.includes('Failed to fetch')) {
        markSheetAsDownloaded(errorSheetUrl);
        return;
      }
      toasterMsg(t('SUPPLIER_OVERVIEW.DOWNLOAD_ERROR_SHEET_FAILED'), 'danger');
    } finally {
      isDownloadAttempted.current = false;
    }
  }, [errorSheetUrl, extractFilenameFromHeaders, generateTimestampedFilename, triggerBlobDownload, getDownloadedSheets, markSheetAsDownloaded, t]);

  const handleClose = useCallback(async () => {
    if (errorSheetUrl) {
      try {
        await downloadErrorSheet();
      } catch {
        // ignore
      }
    }
    onHide();
  }, [downloadErrorSheet, onHide, errorSheetUrl]);

  const handleConfirm = useCallback(async () => {
    if (errorSheetUrl) {
      try {
        await downloadErrorSheet();
      } catch {
        // ignore
      }
    }
    onConfirm();
  }, [downloadErrorSheet, onConfirm, errorSheetUrl]);

  return (
    <BombiixModal
      show={show}
      size="md"
      title={t('SUPPLIER_OVERVIEW.UPLOAD_ERRORS_TITLE')}
      onClose={handleClose}
      onSave={handleConfirm}
      saveLabel={tCommon('COMMON_LABEL.DOWNLOAD')}
      hideSave={!errorSheetUrl}
    >
      <div className="bombiix-modal__confirm-body">
        <p className="mb-0 text-center">{validationMessage ?? t('SUPPLIER_OVERVIEW.UPLOAD_ERRORS_MESSAGE')}</p>
        {errorSheetUrl && <p className="mb-0 text-center">{t('SUPPLIER_OVERVIEW.DOWNLOAD_ERROR_SHEET_PROMPT')}</p>}
      </div>
    </BombiixModal>
  );
}

SupplierUploadErrorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  errorSheetUrl: PropTypes.string,
  validationMessage: PropTypes.string,
};

SupplierUploadErrorModal.defaultProps = {
  errorSheetUrl: null,
  validationMessage: null,
};

export default SupplierUploadErrorModal;
